import PropTypes from 'prop-types';
import { always } from 'ramda';

import { useEffect, useState } from 'react';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Container, Typography } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { FormattedMessage } from 'react-intl';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@classity-edtech/ui-base';
import { SearchInput } from '@classity-edtech/ui-base/dist/components/Inputs';
import { useGetGeoLocationQuery } from '../../services/authentication';
import Countries from '@classity-edtech/ui-base/dist/data/countries.json';
import { AlertComp } from '@classity-edtech/ui-base/dist/components/Displays/AlertComp/AlertComp';
import { ChatIcon } from '@classity-edtech/ui-base/dist/components/Displays';
import { CountryCode } from '@classity-edtech/platform-utils';
import Navigation from '../../components/Navigation';
import { webPaths } from '../../routes/paths';

export interface TuitionProps extends MuiBoxProps {}

export const CountrySelection = ({ className }: TuitionProps) => {
  const screens = useAppMediaQuery();
  const navigate = useNavigate();

  const [country, setCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const hideOptions = !(countrySearch?.length > 1);
  const [showUnsupportedScreen, setShowUnsupportedScreen] = useState(false);

  const refinedCountries = Countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch?.toLowerCase()),
  );

  const { data: location, isLoading: locationLoading } = useGetGeoLocationQuery();

  const handledCountrySearchChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;
    setCountrySearch(target.value);
    setShowUnsupportedScreen(false);
  };

  const handledCountrySearchClear = () => {
    setCountrySearch('');
  };

  const handleSelectCountryOption = (countryCode: string) => {
    setCountry(countryCode);
    setCountrySearch(Countries.find(c => c.code === countryCode)?.name || '');
  };

  const handleContinue = async () => {
    if (country?.toLowerCase() === CountryCode.NG) {
      navigate(`/${webPaths.learnerDashboard}`);
    } else {
      setShowUnsupportedScreen(true);
    }
  };

  const goBack = async () => {
    navigate(`/${webPaths.studentProfile}`);
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  useEffect(() => {
    if (location?.country) {
      setCountry(location?.country);
    }
  }, [location]);

  return (
    <Container sx={{ pb: { xs: 0.5, md: 0 } }}>
      <Navigation
        goBack={goBack}
        continueAction={handleContinue}
        currentStepIndex={1}
        hideInfo
        steps={[]}
      />
      {locationLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: { xs: '100%', md: '568px' }, my: { xs: 3, md: 6 }, mx: 'auto' }}>
          <Typography
            variant={screens.isMobile ? 'subtitle5' : 'subtitle3'}
            sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
          >
            {localisedMessage('screening.country.residence.title')}
          </Typography>
          <Typography
            variant={screens.isMobile ? 'body3' : 'body2'}
            sx={{
              maxWidth: '500px',
              textAlign: { md: 'center' },
              display: 'block',
              mb: 4,
              mx: 'auto',
            }}
          >
            {localisedMessage('screening.country.residence.description')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <SearchInput
              sx={{
                boxShadow: countrySearch?.length > 1 ? '' : 0,
              }}
              removePadding={!(countrySearch?.length > 1)}
              value={country || ''}
              options={[{ values: refinedCountries }]}
              hideOptions={hideOptions}
              searchValue={countrySearch}
              onSearchChange={handledCountrySearchChange}
              onSelectOption={value => handleSelectCountryOption(value as string)}
              clearAction={handledCountrySearchClear}
            />
            {!showUnsupportedScreen ? (
              <Typography
                variant='body3'
                sx={{ color: globalColours['$global-colour-content-tertiary'], px: 2 }}
              >
                {localisedMessage('screening.country.choose')}
              </Typography>
            ) : (
              <Box>
                <AlertComp
                  titleElement={localisedMessage('screening.country.not.supported.title')}
                  description={localisedMessage('screening.country.not.supported.description')}
                  sentiment='warning'
                  handleClose={() => setShowUnsupportedScreen(false)}
                  icon={
                    <ChatIcon
                      sx={theme => ({
                        color: theme?.palette?.common?.black,
                        width: '24px',
                        Height: '24px',
                      })}
                    />
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

CountrySelection.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  nextScreen: PropTypes.func,
};

CountrySelection.defaultProps = {
  children: null,
  className: null,
  nextScreen: always,
};

export default styled<any>(CountrySelection)(
  ({ theme }) => `
  `,
);
